import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_BASE } from '@/api/client';
import {
  Download, FileSpreadsheet, Search, ChevronDown, Heart, Euro,
  CheckCircle2, Clock, TrendingUp, AlertCircle, Mail, Phone, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { useSiteSettings } from '@/context/SiteSettingsContext';

// ---------- Small atoms ----------

function StatCard({ label, value, icon: Icon, tone = 'purple' }) {
  return (
    <div className="card-moj p-5 flex items-center gap-4" data-testid={`donations-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone === 'gold' ? 'bg-moj-gold/20' : 'gradient-purple'}`}>
        <Icon size={22} className={tone === 'gold' ? 'text-moj-gold-deep' : 'text-moj-gold'} />
      </div>
      <div>
        <div className="text-2xl font-bold text-moj-purple-deep">{value}</div>
        <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold">{label}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:      { cls: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
    active:    { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
    initiated: { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    expired:   { cls: 'bg-gray-200 text-gray-700 border-gray-300', icon: AlertCircle },
    cancelled: { cls: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  };
  const m = map[status] || map.initiated;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>
      <Icon size={11} /> {status}
    </span>
  );
}

function fmtMoney(v) {
  const n = Number(v) || 0;
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(n);
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso.toString().slice(0, 16).replace('T', ' '); }
}

// ---------- Row component ----------

function DonationRow({ d }) {
  const [open, setOpen] = useState(false);
  const isRecurring = d.is_recurring;
  const intervalLabel = d.interval === 'week' ? 'weekly' : d.interval === 'month' ? 'monthly' : null;
  return (
    <div className="card-moj overflow-hidden" data-testid={`donation-${d.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 hover:bg-moj-cream-soft transition flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isRecurring ? 'gradient-purple-gold' : 'gradient-purple'}`}>
          <Heart size={16} className="text-moj-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-moj-purple-deep truncate flex items-center gap-2">
            {d.donor_name || 'Anonymous'}
            {isRecurring && (
              <span className="text-[10px] uppercase tracking-wider bg-moj-gold/20 text-moj-gold-deep px-2 py-0.5 rounded-full font-bold">
                {intervalLabel}
              </span>
            )}
          </div>
          <div className="text-xs text-moj-charcoal/60 flex flex-wrap gap-x-3">
            <span>{d.category_label}</span>
            <span>· {d.frequency || 'one_time'}</span>
            <span>· {fmtDateTime(d.created_at)}</span>
            {isRecurring && d.cycles_paid > 0 && <span>· {d.cycles_paid} cycle{d.cycles_paid === 1 ? '' : 's'} paid</span>}
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end text-xs gap-1">
          <div className="font-bold text-moj-purple-deep text-base">
            {fmtMoney(d.amount)}{isRecurring && <span className="text-[10px] font-normal text-moj-charcoal/60"> /{d.interval}</span>}
          </div>
          <StatusBadge status={d.status} />
        </div>
        <ChevronDown size={18} className={`text-moj-gold-deep transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 bg-moj-cream-soft border-t border-moj-gold/20 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="sm:hidden flex items-center justify-between">
            <span className="font-bold text-moj-purple-deep text-base">{fmtMoney(d.amount)}{isRecurring && <span className="text-xs"> /{d.interval}</span>}</span>
            <StatusBadge status={d.status} />
          </div>
          <div><strong className="text-moj-purple-deep">Email:</strong> <a className="text-moj-purple underline" href={`mailto:${d.donor_email}`}><Mail size={11} className="inline" /> {d.donor_email}</a></div>
          {d.donor_phone && <div><strong className="text-moj-purple-deep">Phone:</strong> <a className="text-moj-purple underline" href={`tel:${d.donor_phone}`}><Phone size={11} className="inline" /> {d.donor_phone}</a></div>}
          <div><strong className="text-moj-purple-deep">Created:</strong> {fmtDateTime(d.created_at)}</div>
          <div><strong className="text-moj-purple-deep">First paid:</strong> {fmtDateTime(d.settled_at)}</div>
          {isRecurring && (
            <>
              <div><strong className="text-moj-purple-deep">Subscription status:</strong> {d.subscription_status || '—'}</div>
              <div><strong className="text-moj-purple-deep">Total collected:</strong> {fmtMoney(d.total_paid_amount || 0)} ({d.cycles_paid || 0} cycle{d.cycles_paid === 1 ? '' : 's'})</div>
              <div><strong className="text-moj-purple-deep">Last cycle:</strong> {fmtDateTime(d.last_paid_at)}</div>
              {d.stripe_subscription_id && (
                <div className="sm:col-span-2 break-all"><strong className="text-moj-purple-deep">Stripe Subscription:</strong> <code className="text-[11px]">{d.stripe_subscription_id}</code></div>
              )}
            </>
          )}
          <div className="sm:col-span-2 break-all"><strong className="text-moj-purple-deep">Stripe Session:</strong> <code className="text-[11px]">{d.session_id}</code></div>
          {d.prayer_note && (
            <div className="sm:col-span-2 bg-white border-l-4 border-moj-gold p-3 rounded">
              <div className="text-[10px] uppercase tracking-wider font-bold text-moj-gold-deep mb-1">Prayer Note</div>
              <p className="italic text-moj-purple-deep">{d.prayer_note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Site Settings (Donate page visibility) ----------

function SiteSettingsCard() {
  const qc = useQueryClient();
  const { refresh: refreshPublicSettings } = useSiteSettings();

  const { data: adminSettings, isLoading } = useQuery({
    queryKey: ['adm-app-settings'],
    queryFn: async () => (await api.get('/admin/settings')).data,
  });

  const mutation = useMutation({
    mutationFn: async (next) => (await api.patch('/admin/settings', next)).data,
    onSuccess: (data) => {
      qc.setQueryData(['adm-app-settings'], data);
      // Also refresh the public-facing settings context so the nav/footer
      // reacts without needing a page reload.
      refreshPublicSettings();
      toast.success(
        data.donate_page_visible
          ? 'Donate page is now visible on the website.'
          : 'Donate page is now hidden from the website.',
      );
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Could not update site settings.');
    },
  });

  const visible = adminSettings?.donate_page_visible !== false;
  const Icon = visible ? Eye : EyeOff;

  return (
    <div className="card-moj p-5" data-testid="site-settings-card">
      <div className="flex items-start sm:items-center gap-4 flex-col sm:flex-row sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${visible ? 'bg-moj-gold/20' : 'bg-gray-200'}`}>
            <Icon size={20} className={visible ? 'text-moj-gold-deep' : 'text-moj-charcoal/60'} />
          </div>
          <div>
            <h3 className="font-serif text-lg text-moj-purple-deep">Donate Page Visibility</h3>
            <p className="text-sm text-moj-charcoal/70 mt-0.5">
              {visible
                ? 'The Donate page is live. Visitors can see the Donate link in the header, footer, and open /donate.'
                : 'The Donate page is hidden. The Donate link is removed from the website and /donate redirects to the homepage.'}
            </p>
            <p className="text-xs text-moj-charcoal/50 mt-1">
              Existing Stripe transactions and recurring subscriptions are not affected by this toggle.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 self-end sm:self-center">
          <span className={`text-xs uppercase tracking-wider font-bold ${visible ? 'text-moj-purple-deep' : 'text-moj-charcoal/50'}`}>
            {visible ? 'Visible' : 'Hidden'}
          </span>
          <Switch
            checked={visible}
            disabled={isLoading || mutation.isPending}
            onCheckedChange={(next) => mutation.mutate({ donate_page_visible: next })}
            data-testid="donate-visibility-toggle"
            aria-label="Toggle Donate page visibility"
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Main tab ----------

export default function DonationsTab() {
  const [statusFilter, setStatusFilter] = useState('all'); // all | paid | initiated | expired | cancelled
  const [search, setSearch] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['adm-donations-summary'],
    queryFn: async () => (await api.get('/admin/donations/summary')).data,
  });
  const { data: donations = [], isLoading } = useQuery({
    queryKey: ['adm-donations'],
    queryFn: async () => (await api.get('/admin/donations')).data,
  });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return donations.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (!s) return true;
      return [d.donor_name, d.donor_email, d.donor_phone, d.category_label, d.session_id]
        .some((v) => (v || '').toString().toLowerCase().includes(s));
    });
  }, [donations, statusFilter, search]);

  const downloadXlsx = async (paidOnly = false) => {
    try {
      const url = `${API_BASE}/api/admin/donations/export${paidOnly ? '?status=paid' : ''}`;
      const token = localStorage.getItem('moj_admin_token') || localStorage.getItem('moj_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      const link = window.URL.createObjectURL(blob);
      a.href = link;
      a.download = paidOnly ? 'donations_paid.xlsx' : 'donations_all.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(link);
      toast.success(`Downloaded ${paidOnly ? 'paid' : 'all'} donations`);
    } catch (err) {
      toast.error(err?.message || 'Could not download export.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Site settings — admin can hide/show the public Donate page */}
      <SiteSettingsCard />

      {/* Summary stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Raised" value={fmtMoney(summary?.total_donated_eur || 0)} icon={Euro} />
        <StatCard label="Paid Donations" value={summary?.paid_count || 0} icon={CheckCircle2} tone="gold" />
        <StatCard label="Pending / Initiated" value={summary?.initiated_count || 0} icon={Clock} tone="gold" />
        <StatCard label="All Sessions" value={summary?.total_transactions || 0} icon={TrendingUp} tone="gold" />
      </div>

      {/* By category breakdown */}
      {summary?.by_category?.length > 0 && (
        <div className="card-moj p-5">
          <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Giving by Category</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.by_category.map((c) => (
              <div key={c.category} className="flex items-center justify-between bg-moj-cream-soft rounded-lg px-4 py-2">
                <div className="text-sm">
                  <div className="font-semibold text-moj-purple-deep">{c.category}</div>
                  <div className="text-xs text-moj-charcoal/60">{c.count} gift{c.count === 1 ? '' : 's'}</div>
                </div>
                <div className="font-bold text-moj-purple-deep">{fmtMoney(c.total_eur)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + exports */}
      <div className="card-moj p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-moj-charcoal/40" />
          <input
            className="input-moj pl-9"
            placeholder="Search donor name, email, category, session ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="donations-search"
          />
        </div>
        <select
          className="input-moj sm:max-w-[180px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="donations-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid (one-time)</option>
          <option value="active">Active (recurring)</option>
          <option value="initiated">Pending</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={() => downloadXlsx(true)} className="btn-outline" data-testid="donations-export-paid">
          <FileSpreadsheet size={16} /> Export Paid (XLSX)
        </button>
        <button onClick={() => downloadXlsx(false)} className="btn-primary" data-testid="donations-export-all">
          <Download size={16} /> Export All (XLSX)
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {isLoading && <div className="text-center text-moj-charcoal/60 py-8">Loading donations…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="card-moj p-10 text-center">
            <Heart size={40} className="text-moj-gold-deep mx-auto mb-3" />
            <p className="font-serif text-lg text-moj-purple-deep">No donations match your filters yet.</p>
            <p className="text-sm text-moj-charcoal/70 mt-1">
              {donations.length === 0
                ? 'Once donors make their first gift via /donate, transactions will appear here.'
                : 'Try clearing the search or changing the status filter.'}
            </p>
          </div>
        )}
        {filtered.map((d) => <DonationRow key={d.id} d={d} />)}
      </div>
    </div>
  );
}
